import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getBlogs, updateBlogStatus } from '../services/api';
import { CheckCircle2, XCircle, Clock, Edit3, PlusCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ManageBlogs = () => {
  const { user } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBlogs = async () => {
    setLoading(true);
    let data = await getBlogs("all");
    if (user?.role !== 'admin' && user?.role !== 'editor') {
       data = data.filter(b => Number(b.user_id) === Number(user?.id));
    }
    setBlogs(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleStatusUpdate = async (id, status) => {
    await updateBlogStatus(id, status);
    // Optimistic UI update
    setBlogs(blogs.map(b => b.id === id ? { ...b, status: status } : b));
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-stone-900 mb-2">{user?.role === 'admin' ? "Manage Blog Content" : "My Blogs"}</h1>
          <p className="text-stone-500">{user?.role === 'admin' ? "Review pending articles from users or manage published blocks." : "Check the status of your submitted spiritual articles."}</p>
        </div>
        <Link to="/write" className="flex items-center px-6 py-2.5 bg-saffron-600 text-white rounded-xl font-bold shadow-lg shadow-saffron-600/30 hover:bg-saffron-700 transition">
          <PlusCircle className="h-5 w-5 mr-2" /> Write New Blog
        </Link>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-24 bg-stone-100 rounded-2xl w-full"></div>)}
        </div>
      ) : (
        <div className="overflow-hidden border border-stone-200 rounded-2xl">
          <table className="min-w-full divide-y divide-stone-200">
            <thead className="bg-stone-50">
              <tr>
                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase tracking-wider">Article Title</th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase tracking-wider">Author</th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase tracking-wider">Date</th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase tracking-wider">Status</th>
                <th scope="col" className="px-6 py-4 text-right text-xs font-bold text-stone-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-stone-200">
              {blogs.map((blog) => (
                <tr key={blog.id} className="hover:bg-stone-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-semibold text-stone-900 truncate max-w-xs">{blog.title}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-3 py-1 bg-stone-100 text-stone-600 rounded-lg text-sm font-medium">{blog.author}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                    {blog.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {blog.status === 'published' ? (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Published
                      </span>
                    ) : blog.status === 'pending' ? (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        <Clock className="w-4 h-4 mr-1" /> Pending
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-stone-100 text-stone-800">
                        {blog.status}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex justify-end space-x-2">
                    <Link to={`/write?edit=${blog.id}`} className="text-stone-600 hover:text-saffron-600 hover:bg-saffron-50 bg-stone-100 flex items-center px-3 py-1.5 rounded-lg border border-stone-200 transition">
                         <Edit3 className="w-4 h-4 mr-1"/> Edit
                    </Link>
                    {user?.role === 'admin' || user?.role === 'editor' ? (
                       <>
                        {blog.status === 'pending' && (
                          <>
                            <button onClick={() => handleStatusUpdate(blog.id, 'published')} className="text-green-600 hover:text-green-900 bg-green-50 px-3 py-1.5 rounded-lg border border-green-200">Approve</button>
                            <button onClick={() => handleStatusUpdate(blog.id, 'rejected')} className="text-red-600 hover:text-red-900 bg-red-50 px-3 py-1.5 rounded-lg border border-red-200">Reject</button>
                          </>
                        )}
                        {blog.status === 'published' && (
                          <button onClick={() => handleStatusUpdate(blog.id, 'draft')} className="text-stone-600 hover:text-stone-900 bg-stone-100 px-3 py-1.5 rounded-lg border border-stone-200">Unpublish</button>
                        )}
                       </>
                    ) : null}
                  </td>
                </tr>
              ))}
              {blogs.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-stone-500">No blogs exist yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ManageBlogs;
